import { useEffect, useMemo, useState } from "react";
import { db } from "../../firebase";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import css from "./ProductReviewsAdmin.module.css";

export default function ProductReviewsAdmin() {
  const [reviews, setReviews] = useState([]);
  const [productsMap, setProductsMap] = useState({});
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const fetchData = async () => {
    setLoading(true);

    try {
      const [reviewsSnapshot, productsSnapshot] = await Promise.all([
        getDocs(collection(db, "reviews")),
        getDocs(collection(db, "product")),
      ]);

      const products = {};
      productsSnapshot.docs.forEach((item) => {
        const data = item.data();
        const id = data.uid || item.id;

        products[id] = {
          id,
          bookName: data.bookName || "Без назви",
        };
      });

      const reviewsData = reviewsSnapshot.docs.map((item) => {
        const data = item.data();

        return {
          id: item.id,
          uid: data.uid || item.id,
          productId: data.productId || "",
          productName: data.productName || "",
          vidguk: data.vidguk || "",
          nameAutor: data.nameAutor || "",
          autorPic: data.autorPic || "",
          rating: data.rating || 0,
          userUid: data.userUid || "",
          visible: data.visible !== false,
          createdAt: data.createdAt || null,
          ...data,
        };
      });

      setProductsMap(products);
      setReviews(reviewsData);
    } catch (error) {
      console.error("Помилка завантаження відгуків:", error);
      alert("Не вдалося завантажити відгуки");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredReviews = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) return reviews;

    return reviews.filter((review) => {
      const productName =
        productsMap[review.productId]?.bookName || review.productName || "";

      return (
        productName.toLowerCase().includes(normalizedSearch) ||
        review.vidguk.toLowerCase().includes(normalizedSearch) ||
        review.nameAutor.toLowerCase().includes(normalizedSearch) ||
        review.userUid.toLowerCase().includes(normalizedSearch)
      );
    });
  }, [reviews, productsMap, search]);

  const updateLocalReview = (reviewId, field, value) => {
    setReviews((prev) =>
      prev.map((review) =>
        review.id === reviewId ? { ...review, [field]: value } : review,
      ),
    );
  };

  const handleSaveReview = async (review) => {
    setSavingId(review.id);

    try {
      const reviewRef = doc(db, "reviews", review.id);

      await updateDoc(reviewRef, {
        vidguk: review.vidguk,
        nameAutor: review.nameAutor,
        rating: Number(review.rating) || 0,
        visible: review.visible !== false,
        updatedAt: serverTimestamp(),
      });

      alert("Відгук збережено");
    } catch (error) {
      console.error("Помилка збереження відгуку:", error);
      alert("Не вдалося зберегти відгук");
    } finally {
      setSavingId(null);
    }
  };

  const handleToggleVisible = async (review) => {
    const nextVisible = !review.visible;

    updateLocalReview(review.id, "visible", nextVisible);

    try {
      const reviewRef = doc(db, "reviews", review.id);

      await updateDoc(reviewRef, {
        visible: nextVisible,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Помилка зміни видимості:", error);
      updateLocalReview(review.id, "visible", review.visible);
      alert("Не вдалося змінити видимість");
    }
  };

  const handleDeleteReview = async (reviewId) => {
    const confirmDelete = window.confirm(
      "Видалити цей відгук? Дію не можна буде скасувати.",
    );

    if (!confirmDelete) return;

    try {
      await deleteDoc(doc(db, "reviews", reviewId));

      setReviews((prev) => prev.filter((review) => review.id !== reviewId));
    } catch (error) {
      console.error("Помилка видалення відгуку:", error);
      alert("Не вдалося видалити відгук");
    }
  };

  const formatDate = (value) => {
    if (!value) return "—";

    if (value.toDate) {
      return value.toDate().toLocaleDateString("uk-UA");
    }

    return "—";
  };

  return (
    <div className={css.wrap}>
      <div className={css.header}>
        <div>
          <h2>Відгуки товарів</h2>
          <p>
            Керування відгуками, які користувачі залишають у картках товарів.
          </p>
        </div>

        <button type="button" onClick={fetchData} className={css.reloadButton}>
          Оновити
        </button>
      </div>

      <div className={css.toolbar}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Пошук по товару, автору або тексту відгуку"
          className={css.searchInput}
        />
      </div>

      {loading ? (
        <p>Завантаження...</p>
      ) : filteredReviews.length === 0 ? (
        <p>Відгуків не знайдено.</p>
      ) : (
        <div className={css.list}>
          {filteredReviews.map((review) => {
            const productName =
              productsMap[review.productId]?.bookName ||
              review.productName ||
              "Товар не знайдено";

            return (
              <div key={review.id} className={css.card}>
                <div className={css.cardTop}>
                  <div>
                    <h3>{productName}</h3>
                    <p className={css.meta}>
                      ID товару: {review.productId || "—"}
                    </p>
                    <p className={css.meta}>
                      ID користувача: {review.userUid || "—"}
                    </p>
                    <p className={css.meta}>
                      Дата: {formatDate(review.createdAt)}
                    </p>
                  </div>

                  <label className={css.visibleLabel}>
                    <input
                      type="checkbox"
                      checked={review.visible !== false}
                      onChange={() => handleToggleVisible(review)}
                    />
                    Показувати на сайті
                  </label>
                </div>

                <div className={css.formGrid}>
                  <label>
                    Автор
                    <input
                      type="text"
                      value={review.nameAutor}
                      onChange={(e) =>
                        updateLocalReview(
                          review.id,
                          "nameAutor",
                          e.target.value,
                        )
                      }
                    />
                  </label>

                  <label>
                    Рейтинг
                    <select
                      value={review.rating}
                      onChange={(e) =>
                        updateLocalReview(review.id, "rating", e.target.value)
                      }
                    >
                      <option value="0">0</option>
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                      <option value="5">5</option>
                    </select>
                  </label>
                </div>

                <label className={css.textareaLabel}>
                  Текст відгуку
                  <textarea
                    value={review.vidguk}
                    onChange={(e) =>
                      updateLocalReview(review.id, "vidguk", e.target.value)
                    }
                  />
                </label>

                <div className={css.actions}>
                  <button
                    type="button"
                    onClick={() => handleSaveReview(review)}
                    disabled={savingId === review.id}
                    className={css.saveButton}
                  >
                    {savingId === review.id ? "Збереження..." : "Зберегти"}
                  </button>

                  <button
                    type="button"
                    onClick={() => handleDeleteReview(review.id)}
                    className={css.deleteButton}
                  >
                    Видалити
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
